/**
 * `/evaluations/profiles` — the Evaluations config page listing every
 * learner test profile visible to the caller.
 *
 * A profile bundles the mock learner data + test files a prompt needs to
 * run through the Evaluations flow (formerly "test bed"). This page is
 * the canonical CRUD surface — the per-prompt Evaluations page and the
 * template Prompt Sandbox both link here for create/edit, so the profile
 * data lives in one place instead of duplicated forms.
 */
import { DocumentDuplicateIcon, TrashIcon } from "@heroicons/react/24/outline";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { FC } from "react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Heading, Label, Paragraph } from "@fronterahealth/frontera-ui-components";

import {
  REPORT_TYPE_CHOICES,
  cloneLearnerTestProfile,
  deleteLearnerTestProfile,
  listLearnerTestProfiles,
} from "@api/rest/prompt-test-bed";
import type { LearnerTestProfile, ReportType } from "@api/rest/prompt-test-bed";
import { useBasecampOrganizationsRetrieve } from "@api/rest/types-and-hooks";
import { ALL_ORGS_SENTINEL, OrgOwnerSelect } from "@components/Prompts/OrgOwnerSelect";
import { GLOBAL_ORG_SENTINEL } from "@components/Prompts/constants";
import { notifyError, notifySuccess } from "@components/notifications/notifications";

// Sentinel for the report-type filter's "any" option.
const REPORT_TYPE_ALL = "__all__";

function reportTypeLabels(profile: LearnerTestProfile): string {
  if (!profile.report_types?.length) return "Global";
  const labels = profile.report_types.map((v) => REPORT_TYPE_CHOICES.find((c) => c.value === v)?.label ?? v);
  return labels.join(", ");
}

function ProfileRow({
  profile,
  orgName,
  onDelete,
  onDuplicate,
  duplicating,
  onOpen,
}: {
  profile: LearnerTestProfile;
  orgName: string;
  onDelete: (id: number) => void;
  onDuplicate: (profile: LearnerTestProfile) => void;
  duplicating: boolean;
  onOpen: (id: number) => void;
}) {
  return (
    <tr
      onClick={() => onOpen(profile.id)}
      className="border-b border-border-subtle last:border-b-0 hover:bg-bg-default cursor-pointer"
    >
      <td className="px-4 py-3">
        <span className="text-sm font-medium text-text-primary">{profile.name}</span>
        {profile.notes && <div className="text-xs text-text-tertiary mt-0.5 line-clamp-1">{profile.notes}</div>}
      </td>
      <td className="px-4 py-3 text-xs text-text-secondary">{orgName}</td>
      <td className="px-4 py-3 text-xs text-text-secondary">{reportTypeLabels(profile)}</td>
      <td className="px-4 py-3 text-xs text-text-tertiary">
        {profile.test_files.length} files, {Object.keys(profile.mock_learner_data ?? {}).length} inputs
      </td>
      <td className="px-4 py-3 text-xs text-text-tertiary">
        {new Date(profile.last_updated_date).toLocaleDateString()}
      </td>
      <td className="px-4 py-3 text-right">
        <div className="flex justify-end gap-1">
          <button
            type="button"
            aria-label={`Duplicate ${profile.name}`}
            title="Duplicate"
            disabled={duplicating}
            onClick={(e) => {
              e.stopPropagation();
              onDuplicate(profile);
            }}
            className="p-1.5 rounded text-text-tertiary hover:text-text-secondary hover:bg-bg-default transition-colors disabled:opacity-50"
          >
            <DocumentDuplicateIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            aria-label={`Delete ${profile.name}`}
            onClick={(e) => {
              // Stop propagation so the row's onClick doesn't ALSO navigate
              // to the edit page while the confirmation modal is opening.
              e.stopPropagation();
              onDelete(profile.id);
            }}
            className="p-1.5 rounded text-red-600 hover:text-red-700 hover:bg-red-50 transition-colors disabled:opacity-50"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      </td>
    </tr>
  );
}

export const LearnerTestProfilesList: FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [pendingDelete, setPendingDelete] = useState<LearnerTestProfile | null>(null);
  // Uses OrgOwnerSelect's sentinels: ALL_ORGS_SENTINEL = match every,
  // GLOBAL_ORG_SENTINEL = null-org only, else a stringified org id.
  const [orgFilter, setOrgFilter] = useState<string>(ALL_ORGS_SENTINEL);
  const [reportTypeFilter, setReportTypeFilter] = useState<string>(REPORT_TYPE_ALL);

  const { data: profiles = [], isLoading } = useQuery({
    queryKey: ["learner-test-profiles"],
    queryFn: () => listLearnerTestProfiles(),
  });

  const { data: orgsResponse } = useBasecampOrganizationsRetrieve({});
  const orgs = useMemo(
    () =>
      orgsResponse?.status === 200 ? (Array.isArray(orgsResponse.data) ? orgsResponse.data : [orgsResponse.data]) : [],
    [orgsResponse],
  );
  const orgNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const o of orgs) map.set(Number(o.id), o.name);
    return map;
  }, [orgs]);
  const orgLabel = (id: number | null) => (id == null ? "Global" : (orgNameById.get(id) ?? `Org ${id}`));

  const filteredProfiles = useMemo(() => {
    return profiles.filter((p) => {
      if (orgFilter === GLOBAL_ORG_SENTINEL) {
        if (p.organization_id != null) return false;
      } else if (orgFilter !== ALL_ORGS_SENTINEL) {
        if (String(p.organization_id) !== orgFilter) return false;
      }
      if (reportTypeFilter !== REPORT_TYPE_ALL) {
        // Strict match — a picked filter means "profiles that name this
        // report type", not "profiles that could apply to it". Global
        // (empty report_types) is still shown under "All".
        if (!p.report_types?.includes(reportTypeFilter as ReportType)) return false;
      }
      return true;
    });
  }, [profiles, orgFilter, reportTypeFilter]);

  // Unscoped copy so it lands in this list; opens the editor to rename.
  const duplicateMutation = useMutation({
    mutationFn: (profile: LearnerTestProfile) =>
      cloneLearnerTestProfile(profile.id, { name: `${profile.name} (copy)`, scoped_prompt_key: null }),
    onSuccess: async (copy) => {
      await queryClient.invalidateQueries({ queryKey: ["learner-test-profiles"] });
      notifySuccess(`Duplicated as “${copy.name}”.`);
      navigate(`/evaluations/profiles/${copy.id}`);
    },
    onError: (e: unknown) => notifyError(e instanceof Error ? e.message : "Couldn’t duplicate the profile."),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteLearnerTestProfile(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["learner-test-profiles"] });
      setPendingDelete(null);
    },
  });

  return (
    <div className="flex flex-col bg-bg-default min-h-full px-4 sm:px-8 lg:px-16 py-8">
      <div className="flex flex-row items-center justify-between gap-3 mb-6 flex-wrap">
        <div className="flex flex-col gap-1">
          <Heading size="medium">Learner Profiles</Heading>
          <Paragraph size="small" colorType="secondary">
            Reusable bundles of mock learner data + test files. Pick one when running an evaluation on the per-prompt
            page or in the template Prompt Sandbox.
          </Paragraph>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => navigate("/evaluations/profiles/new")}
            className="text-sm px-3 py-2 rounded-md bg-limestone-900 text-white hover:bg-limestone-800 transition-colors"
          >
            + Add Profile
          </button>
        </div>
      </div>

      {isLoading ? null : profiles.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 py-16 rounded-xl border border-border-medium bg-bg-surface text-center">
          <Label size="small" colorType="secondary" className="uppercase tracking-wide">
            No profiles yet
          </Label>
          <Paragraph colorType="secondary" className="max-w-lg">
            Create your first learner test profile to run prompt evaluations. Profiles bundle mock demographic data plus
            a set of test files the prompt can read from.
          </Paragraph>
        </div>
      ) : (
        // No `overflow-hidden` here — the Organization filter's absolute
        // dropdown needs to escape past the table bounds. The table's own
        // wrapper below reintroduces overflow clipping + bottom-corner
        // rounding so nothing renders past the rounded frame.
        <div className="border border-border-medium rounded-xl bg-bg-surface">
          <div className="flex flex-wrap items-end gap-3 px-4 py-3 border-b border-border-subtle bg-bg-default rounded-t-xl">
            <OrgOwnerSelect orgs={orgs} value={orgFilter} onChange={setOrgFilter} label="Organization" allowAll />
            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium text-text-secondary">Report type</span>
              <select
                value={reportTypeFilter}
                onChange={(e) => setReportTypeFilter(e.target.value)}
                className="rounded-md border border-border-medium bg-bg-surface px-3 py-1.5 text-sm text-text-primary"
              >
                <option value={REPORT_TYPE_ALL}>All</option>
                {REPORT_TYPE_CHOICES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
            <span className="ml-auto text-xs text-text-tertiary">
              {filteredProfiles.length} of {profiles.length}
            </span>
          </div>

          <div className="overflow-hidden rounded-b-xl">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-text-tertiary border-b border-border-subtle bg-bg-default">
                  <th className="px-4 py-2 font-medium">Name</th>
                  <th className="px-4 py-2 font-medium">Organization</th>
                  <th className="px-4 py-2 font-medium">Report types</th>
                  <th className="px-4 py-2 font-medium">Contents</th>
                  <th className="px-4 py-2 font-medium">Updated</th>
                  <th className="px-4 py-2" aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {filteredProfiles.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-6 text-center text-xs italic text-text-tertiary">
                      No profiles match the current filters.
                    </td>
                  </tr>
                ) : (
                  filteredProfiles.map((p) => (
                    <ProfileRow
                      key={p.id}
                      profile={p}
                      orgName={orgLabel(p.organization_id)}
                      onOpen={(id) => navigate(`/evaluations/profiles/${id}`)}
                      onDelete={(id) => setPendingDelete(profiles.find((x) => x.id === id) ?? null)}
                      onDuplicate={(profile) => duplicateMutation.mutate(profile)}
                      duplicating={duplicateMutation.isPending}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {pendingDelete && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={() => setPendingDelete(null)}
        >
          <div
            className="bg-bg-surface rounded-xl p-6 max-w-md w-full flex flex-col gap-4"
            onClick={(e) => e.stopPropagation()}
          >
            <Heading size="small">Delete profile?</Heading>
            <Paragraph colorType="secondary">
              &ldquo;{pendingDelete.name}&rdquo; will be removed. Runs that already used it stay in history but their
              profile link becomes null.
            </Paragraph>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setPendingDelete(null)}
                className="text-sm px-3 py-2 rounded-md border border-border-medium bg-bg-default hover:bg-bg-hover"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => deleteMutation.mutate(pendingDelete.id)}
                disabled={deleteMutation.isPending}
                className="text-sm px-3 py-2 rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMutation.isPending ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
